import React, { useState, useEffect } from 'react';
import StudentInfo from './StudentInfo';
import FeeDetails from './FeeDetails';
import PaymentSection from './PaymentSection';
import RevaluationModal from './RevaluationModal';
import { postData, getData } from '../../../../services/apiService';
import { useSelector } from 'react-redux';

const ExamFees = () => {
	const [isProcessing, setIsProcessing] = useState(false);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [studentData, setStudentData] = useState(null);
	const [feeData, setFeeData] = useState([]);
	const [galleryDetails, setGalleryDetails] = useState([]);
	const [summaryData, setSummaryData] = useState(null);
	const [showGatewayModal, setShowGatewayModal] = useState(false);
	const [selectedGateway, setSelectedGateway] = useState(null);

	const [showRevaluationModal, setShowRevaluationModal] = useState(false);
	const [orderId, setOrderId] = useState(null);
	const [receiptLoading, setReceiptLoading] = useState(false);
	const [receiptDone, setReceiptDone] = useState(false);
	const [showDetails, setShowDetails] = useState(false);
	const user = useSelector((state) => state.user.user);

	const paymentGateways = [
		{
			id: 'ccavenue',
			name: 'CCAvenue',
			icon: '🏦',
			description: 'Pay via CCAvenue Secure Gateway',
			color: 'from-blue-500 to-indigo-500'
		}
	];
	useEffect(() => {
		if (!user) return;

		const fetchExamFee = async () => {
			const reqBody = {
				proc_Year: user.procYear,
				inst_Id: user.inst_id,
				proc_Sem: user.procSem,
				roll_No: user.roll_no,
				sem_No: user.semNo
			};

			try {
				const response = await postData(`/Fees/GetFeesDetails`, reqBody);

				if (!(response?.status === true && response?.feeDetails)) {
					setError(response?.message || 'No data received');
					return;
				}

				const s = response.summary;
				const student = {
					name: s?.name || '',
					enrollmentNo: s?.roll_No || '',
					course: s?.cour_LName || '',
					examName: 'Even Semester Examination',
					semester: `Semester ${s?.sem_No}`,
					academicYear: `${s?.proc_Year || 2024}-${(s?.proc_Year || 2024) + 1}`,
					email: s?.email,
					phone: s?.phone,
					rollNo: s?.roll_No,
					address: s?.address || '',
					city: s?.city || '',
					state: s?.state || ' ',
					zip: s?.zip || ''
				};

				let paidOrderId = null;
				try {
					const custId = student.enrollmentNo || user.roll_no;
					if (custId) {
						const orderRes = await getData(`/payment/order-id?custId=${custId}`);
						const hasOrderId =
							orderRes?.status === true &&
							!!orderRes?.orderId &&
							!orderRes?.isError &&
							orderRes?.success !== false;
						if (hasOrderId) paidOrderId = orderRes.orderId;
					}
				} catch (err) {
					console.error('Layout decision failed:', err);
				}

				setStudentData(student);
				setFeeData(response.feeDetails || []);
				setGalleryDetails(response.galleryDetails || []);
				setSummaryData(s || null);
				setOrderId(paidOrderId);
				setReceiptDone(false);
				setShowDetails(!paidOrderId);
			} catch (error) {
				console.error('Error fetching fees:', error);
				setError(error.message || 'An error occurred while fetching fee details');
			} finally {
				setLoading(false);
			}
		};

		fetchExamFee();
	}, [user]);

	const totalFee = Number(summaryData?.totalFee || 0);

	const getDisplayCategories = () =>
		(feeData || [])
			.filter((f) => Number(f.finalFees) > 0)
			.map((f) => ({
				code: f.fee_Code,
				name: f.fee_Desc,
				category: f.feeCategory,
				amount: Number(f.finalFees)
			}));

	const handlePayNow = () => {
		if (totalFee === 0) return;
		setShowGatewayModal(true);
	};

	const handleDownloadReceipt = async () => {
		try {
			setReceiptLoading(true);
			setError(null);

			let currentOrderId = orderId;

			if (!currentOrderId) {
				const custId = studentData?.enrollmentNo || user?.roll_no;
				if (!custId) throw new Error('Customer ID not available');

				const orderRes = await getData(`/payment/order-id?custId=${custId}`);

				const hasOrderId =
					orderRes?.status === true &&
					!!orderRes?.orderId &&
					!orderRes?.isError &&
					orderRes?.success !== false;

				if (!hasOrderId) {
					setError(orderRes?.message || 'No student data found.');
					setShowDetails(true);
					return;
				}

				currentOrderId = orderRes.orderId;
				setOrderId(currentOrderId);
			}

			const receiptRes = await getData(
				`/payment/payment-receipt?order_id=${currentOrderId}&format=pdf`,
				'blob'
			);

			if (receiptRes?.isError) {
				throw new Error(receiptRes.message || 'Unable to download receipt');
			}

			const blob = new Blob([receiptRes.data], { type: 'application/pdf' });
			const blobUrl = URL.createObjectURL(blob);

			const link = document.createElement('a');
			link.href = blobUrl;
			link.download = `Receipt_${currentOrderId}.pdf`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);

			setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);

			setReceiptDone(true);
		} catch (err) {
			console.error('Receipt download failed:', err);
			setError(err.message || 'Failed to download receipt');
			setShowDetails(true);
		} finally {
			setReceiptLoading(false);
		}
	};

	const handleGatewaySelect = async (gateway) => {
		setSelectedGateway(gateway);
		setShowGatewayModal(false);
		setIsProcessing(true);
		setError(null);

		try {
			if (gateway.id === 'easebuzz') {
				await handleEasebuzzPayment();
			} else if (gateway.id === 'ccavenue') {
				await handleCCAvenuePayment();
			} else if (gateway.id === 'paytm') {
				await handlePaytmPayment();
			}
		} catch (error) {
			console.error('Payment initiation failed:', error);
			setError(error.message || 'Payment failed. Please try again.');
			setIsProcessing(false);
			setSelectedGateway(null);
		} finally {
			setIsProcessing(false);
		}
	};

	const handleEasebuzzPayment = async () => {
		const paymentRequestBody = {
			amount: totalFee,
			firstName: studentData?.name || '',
			email: studentData?.email || '',
			phone: studentData?.phone || '',
			rollNo: studentData?.rollNo,
			gateway: 'easebuzz'
		};

		const response = await postData('/Fees/InitiatePayment', paymentRequestBody);

		if (response?.success && response?.paymentUrl) {
			sessionStorage.setItem(
				'paymentDetails',
				JSON.stringify({
					amount: totalFee,
					rollNo: studentData?.rollNo,
					transactionId: response.transactionId || 'TXN' + Date.now()
				})
			);
			window.location.href = response.paymentUrl;
		} else {
			throw new Error('Failed to initiate Easebuzz payment');
		}
	};

	const handleCCAvenuePayment = async () => {
		try {
			setIsProcessing(true);
			setError(null);

			const paymentData = {
				orderId: `ORD${Date.now()}`,
				amount: totalFee.toString(),
				currency: 'INR',
				userCatg: 'STUD',
				instId: user?.inst_id || 8,
				userCatgId: user?.userCatgId,
				custId: studentData?.rollNo || user?.roll_no || '',
				semNo: user?.semNo || 0,
				procYear: user?.procYear || 2026,
				procSem: user?.procSem || 1,
				billingName: studentData?.name || user?.name || '',
				billingAddress: studentData?.address || '',
				billingCity: studentData?.city || '',
				billingState: studentData?.state || '',
				billingZip: studentData?.zip || '',
				billingCountry: 'India',
				billingTel: studentData?.phone || user?.phone || '',
				billingEmail: studentData?.email || user?.email || ''
			};
			const response = await postData('/payment/initiate', paymentData);

			if (response?.success && response?.encRequest && response?.accessCode) {
				const form = document.createElement('form');
				form.method = 'POST';
				form.action =
					response.checkoutUrl ||
					'https://secure.ccavenue.com/transaction/transaction.do?command=initiateTransaction';
				form.target = '_self';

				const params = {
					encRequest: response.encRequest,
					access_code: response.accessCode
				};

				Object.keys(params).forEach((key) => {
					const input = document.createElement('input');
					input.type = 'hidden';
					input.name = key;
					input.value = params[key];
					form.appendChild(input);
				});

				document.body.appendChild(form);
				form.submit();

				setTimeout(() => {
					if (document.body.contains(form)) {
						document.body.removeChild(form);
					}
				}, 1000);
			} else {
				throw new Error(response?.message || 'Failed to initiate CCAvenue payment');
			}
		} catch (error) {
			console.error('CCAvenue payment error:', error);
			setIsProcessing(false);
			setSelectedGateway(null);
			setError(error.message || 'Payment initiation failed. Please try again.');
		}
	};

	const handlePaytmPayment = async () => {
		const paymentRequestBody = {
			amount: totalFee,
			firstName: studentData?.name || '',
			email: studentData?.email || '',
			phone: studentData?.phone || '',
			rollNo: studentData?.rollNo,
			gateway: 'paytm'
		};

		const response = await postData('/Fees/InitiatePaytmPayment', paymentRequestBody);

		if (response?.success && response?.paymentUrl) {
			sessionStorage.setItem(
				'paymentDetails',
				JSON.stringify({
					amount: totalFee,
					rollNo: studentData?.rollNo,
					transactionId: response.transactionId || 'TXN' + Date.now()
				})
			);
			window.location.href = response.paymentUrl;
		} else {
			throw new Error('Failed to initiate Paytm payment');
		}
	};

	const closeGatewayModal = () => {
		setShowGatewayModal(false);
		setSelectedGateway(null);
	};

	const openRevaluationModal = () => {
		setShowRevaluationModal(true);
	};

	const closeRevaluationModal = () => {
		setShowRevaluationModal(false);
	};

	const handleRevaluationPaymentSuccess = (paymentData) => {
		alert(
			`Revaluation payment of ₹${paymentData.totalFee} initiated successfully for ${paymentData.subjects.length} subjects!`
		);
	};

	const galleryCounts = {
		regularCount: galleryDetails.filter((g) => g.ilrS_TAG === 'R').length,
		supplementaryCount: galleryDetails.filter((g) => g.ilrS_TAG === 'S').length,
		totalCount: galleryDetails.length
	};

	if (loading) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-[#0d4f5c]/10 via-white to-[#1a7a8a]/10 flex items-center justify-center">
				<div className="text-center">
					<div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#0d4f5c] mx-auto mb-4"></div>
					<p className="text-gray-600">Loading fee details...</p>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gradient-to-br from-[#0d4f5c]/10 via-white to-[#1a7a8a]/10 p-4 md:p-8">
			<div className="max-w-full mx-auto">
				{error && !showDetails && (
					<div className="mb-6 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
						{error}
					</div>
				)}

				<StudentInfo student={studentData} />

				{!showDetails && (
					<div className="flex justify-center mt">
						<button
							onClick={handleDownloadReceipt}
							disabled={receiptLoading || receiptDone}
							style={
								!receiptLoading && !receiptDone ? { animation: 'glow 1.5s infinite' } : undefined
							}
							className={`relative overflow-hidden text-lg inline-flex items-center gap-1
                              text-white font-semibold px-6 py-4 rounded-xl shadow 
                              transition active:scale-95 active:brightness-125 
                              disabled:opacity-60 disabled:cursor-not-allowed
                              ${
																receiptDone
																	? 'bg-green-600'
																	: 'bg-gradient-to-r from-[#0d4f5c] to-[#1a7a8a] hover:from-[#0a3f4a] hover:to-[#156a78]'
															}
                        `}
						>
							<style>{`
                        @keyframes shine {
                           0% { left: -60%; }
                           60%, 100% { left: 140%; }
                        }

                        @keyframes glow {
                           0%, 100% {
                              box-shadow: 0 0 0 0 rgba(26,122,138,.7);
                           }

                           50% {
                              box-shadow: 0 0 0 10px rgba(26,122,138,0);
                           }
                        }
                        `}</style>

							{!receiptLoading && !receiptDone && (
								<span
									className="pointer-events-none absolute top-0 bottom-0 w-1/3 
                           -skew-x-12 bg-gradient-to-r from-transparent 
                           via-white/50 to-transparent"
									style={{
										left: '-60%',
										animation: 'shine 2s ease-in-out infinite'
									}}
								/>
							)}

							{receiptLoading ? (
								<>
									<span className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white" />
									Downloading receipt...
								</>
							) : receiptDone ? (
								<>
									<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
											d="M5 13l4 4L19 7"
										/>
									</svg>
									Receipt Downloaded
								</>
							) : (
								<>
									<svg
										className="w-6 h-6 animate-bounce"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
											d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
										/>
									</svg>
									Download Receipt
								</>
							)}
						</button>
					</div>
            )}
            
				{studentData && showDetails && (
					<>
						<FeeDetails
							academicYear={studentData.academicYear}
							totalFee={totalFee}
							summary={summaryData}
							categories={getDisplayCategories()}
						/>

						{}
						{galleryDetails.length > 0 && (
							<div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
								<div className="flex items-center gap-3 mb-5 pb-3 border-b border-gray-200">
									<svg
										className="w-6 h-6 text-[#0d4f5c]"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
											d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
										/>
									</svg>
									<h2 className="text-xl font-bold text-gray-800">Registered Subjects</h2>
								</div>

								<div className="overflow-x-auto">
									<table className="w-full text-sm">
										<thead>
											<tr className="bg-gradient-to-r from-[#0d4f5c] to-[#1a7a8a] text-white">
												<th className="px-4 py-3 text-left font-semibold rounded-tl-lg">S.No</th>
												<th className="px-4 py-3 text-left font-semibold">Subject Code</th>
												<th className="px-4 py-3 text-left font-semibold">Subject Name</th>
												<th className="px-4 py-3 text-center font-semibold rounded-tr-lg">Type</th>
											</tr>
										</thead>
										<tbody>
											{galleryDetails.map((item, index) => (
												<tr
													key={index}
													className={`border-b border-gray-100 hover:bg-gray-50 transition ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
												>
													<td className="px-4 py-3 text-gray-600">{index + 1}</td>
													<td className="px-4 py-3 font-mono text-gray-700">{item.subJ_CODE}</td>
													<td className="px-4 py-3 text-gray-800 font-medium">{item.subJ_NAME}</td>
													<td className="px-4 py-3 text-center">
														<span
															className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
																item.ilrS_TAG === 'R'
																	? 'bg-green-100 text-green-700'
																	: 'bg-orange-100 text-orange-700'
															}`}
														>
															{item.ilrS_TAG === 'R' ? 'Regular' : 'Supplementary'}
														</span>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>

								<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
									<div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-xl p-4">
										<div className="flex items-center justify-between">
											<div>
												<p className="text-xs font-semibold text-green-700 uppercase">
													Regular Papers
												</p>
												<p className="text-2xl font-bold text-green-800 mt-1">
													{galleryCounts.regularCount}
												</p>
											</div>
										</div>
									</div>

									<div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-xl p-4">
										<div className="flex items-center justify-between">
											<div>
												<p className="text-xs font-semibold text-orange-700 uppercase">
													Supplementary Papers
												</p>
												<p className="text-2xl font-bold text-orange-800 mt-1">
													{galleryCounts.supplementaryCount}
												</p>
											</div>
										</div>
									</div>

									<div className="bg-gradient-to-br from-[#0d4f5c]/10 to-[#1a7a8a]/20 border border-[#0d4f5c]/30 rounded-xl p-4">
										<div className="flex items-center justify-between">
											<div>
												<p className="text-xs font-semibold text-[#0d4f5c] uppercase">
													Total Papers
												</p>
												<p className="text-2xl font-bold text-[#0d4f5c] mt-1">
													{galleryCounts.totalCount}
												</p>
											</div>
										</div>
									</div>
								</div>
							</div>
						)}

						<PaymentSection
							totalFee={totalFee}
							isProcessing={isProcessing}
							handlePayNow={handlePayNow}
							examName={studentData.examName}
							disabled={totalFee === 0}
							error={error}
						/>
					</>
				)}
			</div>

			{showDetails && showGatewayModal && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
					<div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 animate-fadeIn">
						<div className="flex justify-between items-center mb-6">
							<h2 className="text-2xl font-bold text-gray-800">Select Payment Gateway</h2>
							<button
								onClick={closeGatewayModal}
								className="text-gray-500 hover:text-gray-700 transition"
							>
								<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth="2"
										d="M6 18L18 6M6 6l12 12"
									/>
								</svg>
							</button>
						</div>

						<div className="space-y-4">
							{paymentGateways.map((gateway) => (
								<button
									key={gateway.id}
									onClick={() => handleGatewaySelect(gateway)}
									className="w-full flex items-center gap-4 p-4 border border-gray-200 rounded-xl hover:border-transparent hover:shadow-lg transition-all duration-200 group"
								>
									<div
										className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl bg-gradient-to-r ${gateway.color}`}
									>
										{gateway.icon}
									</div>
									<div className="flex-1 text-left">
										<h3 className="font-semibold text-gray-800 group-hover:text-[#0d4f5c] transition">
											{gateway.name}
										</h3>
										<p className="text-sm text-gray-600">{gateway.description}</p>
									</div>
									<svg
										className="w-5 h-5 text-gray-400 group-hover:text-[#0d4f5c] transition-transform group-hover:translate-x-1"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
											d="M9 5l7 7-7 7"
										/>
									</svg>
								</button>
							))}
						</div>

						<div className="mt-6 pt-4 border-t border-gray-200">
							<p className="text-xs text-gray-500 text-center">
								Total Amount: ₹{totalFee.toFixed(2)} • All transactions are secure and encrypted
							</p>
						</div>
					</div>
				</div>
			)}

			{}
			{showDetails && (
				<RevaluationModal
					isOpen={showRevaluationModal}
					onClose={closeRevaluationModal}
					onPaymentSuccess={handleRevaluationPaymentSuccess}
					user={user}
					studentData={studentData}
				/>
			)}

			{}
			{showDetails && isProcessing && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
					<div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full text-center">
						<div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#0d4f5c] mx-auto mb-4"></div>
						<h3 className="text-lg font-semibold text-gray-800 mb-2">Processing Payment</h3>
						<p className="text-sm text-gray-600">
							Please wait while we redirect you to {selectedGateway?.name || 'payment gateway'}...
						</p>
					</div>
				</div>
			)}
		</div>
	);
};

export default ExamFees;
